import { useLayoutEffect, useRef } from "react";
import { Badge, IconButton, Popover, styled, useMediaQuery } from "@mui/material";
import { keyframes } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";

import useAuthorization from "../../contexts/useAuthorization";
import useNotifications from "./NotificationsContext";
import NotificationPanel from "./NotificationPanel";
import PanelTransition from "./PanelTransition";
import {
  BADGE_POP_MS,
  EASE_BOUNCE,
  FRAME_SLACK_MS,
  hoverTint,
  PANEL_CLOSE_MS,
  PANEL_OPEN_MS,
  prefersReducedMotion,
} from "./motion";

// Subtle attention "ring" when the badge has unread items. One-shot per mount /
// count change, compositor-only (transform), and disabled under reduced motion.
const bellRing = keyframes`
  0%, 60%, 100% { transform: rotate(0deg); }
  10%, 30%      { transform: rotate(-12deg); }
  20%, 40%      { transform: rotate(12deg); }
`;

// Badge pop when the unread count goes *up* (a new notification), never on a
// decrease: the number re-enters from half size on --ease-bounce over
// --duration-very-slow (the badge-appear tokens), like a number pop-in without the
// blur. The keyframes restate MUI's own `translate(50%, -50%)` for a top-right
// circular badge; scaling around its origin (100% 0%) keeps the dot centred.
const badgePop = keyframes`
  0%   { opacity: 0; transform: scale(0.5) translate(50%, -50%); }
  100% { opacity: 1; transform: scale(1) translate(50%, -50%); }
`;
const BADGE_POP_CLASS = "notif-badge-pop";

// Popover open/close timing (see PanelTransition). Reduced motion: 0, i.e. instant.
const PANEL_TRANSITION_DURATION = prefersReducedMotion ? 0 : { enter: PANEL_OPEN_MS, exit: PANEL_CLOSE_MS };

export const NotificationBell = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { loggedIn } = useAuthorization();
  const { unreadCount, isOpen, open, close } = useNotifications();

  const anchorRef = useRef<HTMLButtonElement | null>(null);

  // Replay the pop on every increase. Imperative (class + will-change on the MUI
  // badge node) so it can restart mid-animation without remounting the bell, which
  // would also replay the ring. Layout effect: when the count goes 0 → n, the class
  // must land in the same frame MUI drops `invisible`, and the pop class also sets
  // `transition: none` so MUI's own scale-in transition doesn't fight the keyframes.
  const previousUnread = useRef(unreadCount);
  const stopPop = useRef<(() => void) | null>(null);
  useLayoutEffect(() => {
    const grew = unreadCount > previousUnread.current;
    previousUnread.current = unreadCount;
    if (!grew || prefersReducedMotion) {
      return;
    }
    const badge = anchorRef.current?.querySelector<HTMLElement>(".MuiBadge-badge");
    if (!badge) {
      return;
    }

    stopPop.current?.();
    if (badge.classList.contains(BADGE_POP_CLASS)) {
      badge.classList.remove(BADGE_POP_CLASS);
      void badge.offsetWidth; // restart the keyframes
    }
    badge.style.willChange = "transform, opacity";
    badge.classList.add(BADGE_POP_CLASS);

    let done = false;
    const onEnd = (event: AnimationEvent) => {
      if (event.target === badge) {
        finish();
      }
    };
    const finish = () => {
      if (done) {
        return;
      }
      done = true;
      badge.removeEventListener("animationend", onEnd);
      window.clearTimeout(timer);
      badge.classList.remove(BADGE_POP_CLASS);
      badge.style.willChange = "";
      stopPop.current = null;
    };
    badge.addEventListener("animationend", onEnd);
    // Fallback: reduced-motion CSS (setting flipped mid-session) never fires animationend.
    const timer = window.setTimeout(finish, BADGE_POP_MS + FRAME_SLACK_MS);
    stopPop.current = finish;
  }, [unreadCount]);

  // Only logged-in users have a notification feed.
  if (!loggedIn) return null;

  const handleToggle = () => (isOpen ? close() : open());

  const bellButton = (
    <BellButton
      ref={anchorRef}
      onClick={handleToggle}
      aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      size="medium"
    >
      <Badge
        badgeContent={unreadCount}
        max={99}
        color="error"
        overlap="circular"
        sx={{
          "& .MuiBadge-badge": {
            fontSize: "10px",
            minWidth: "16px",
            height: "16px",
            padding: "0 4px",
            fontWeight: 600,
          },
          [`& .MuiBadge-badge.${BADGE_POP_CLASS}`]: {
            transition: "none",
            animation: `${badgePop} ${BADGE_POP_MS}ms ${EASE_BOUNCE} both`,
          },
          "@media (prefers-reduced-motion: reduce)": {
            [`& .MuiBadge-badge.${BADGE_POP_CLASS}`]: { animation: "none" },
          },
        }}
      >
        <NotificationsNoneOutlinedIcon
          sx={{
            width: "22px",
            height: "22px",
            color: "black",
            transformOrigin: "top center",
            ...(unreadCount > 0 && !prefersReducedMotion ? { animation: `${bellRing} 0.9s ease-in-out` } : {}),
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      </Badge>
    </BellButton>
  );

  return (
    <>
      {bellButton}

      {/* Same anchored popover on every breakpoint — the panel itself is
          responsive (min(480px, calc(100vw - 24px))), so mobile gets the exact
          desktop feed dropping from the bell rather than a separate bottom sheet.
          The only mobile-specific behaviour is swipe-left-to-clear on each row.

          Open/close is a single transition on the paper (PanelTransition in the
          TransitionComponent slot): it grows from the bell corner (transformOrigin
          below) and, unlike the old Grow + panel keyframe pair, animates the close. */}
      <Popover
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={close}
        TransitionComponent={PanelTransition}
        transitionDuration={PANEL_TRANSITION_DURATION}
        // Desktop only: nudge 24px past the bell's own right edge (40px wide,
        // see BellButton below) so the panel isn't flush with the button.
        anchorOrigin={{ vertical: "bottom", horizontal: isMobile ? "right" : 40 + 24 }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        marginThreshold={0}
        slotProps={{
          paper: {
            sx: {
              mt: "10px",
              borderRadius: "20px",
              border: "none",
              width: "auto",
              overflow: "visible",
              backgroundColor: "transparent",
              boxShadow: "rgba(0, 0, 0, 0.18) 0px 12px 32px 0px",
              // CSS half of the reduced-motion guard (JS passes duration 0): also
              // covers the OS setting flipping after the module-level probe ran.
              "@media (prefers-reduced-motion: reduce)": {
                transition: "none !important",
                animation: "none",
              },
            },
          },
        }}
      >
        <NotificationPanel onNavigate={close} swipeToDismiss={isMobile} />
      </Popover>
    </>
  );
};

// Matches the header's round icon-button affordance (e.g. the search button):
// 40px circular hit area with a soft hover state on the white toolbar. The hover
// circle fades on opacity (hoverTint's ::before) instead of MUI's background-color
// transition; same tint in both modes, as before.
const BellButton = styled(IconButton)(({ theme }) => ({
  width: "40px",
  height: "40px",
  color: "black",
  borderRadius: "50%",
  flexShrink: 0,
  ...hoverTint(theme, { light: "rgba(0, 0, 0, 0.06)", dark: "rgba(0, 0, 0, 0.06)" }),
  "&:hover": {
    backgroundColor: "transparent",
  },
}));

export default NotificationBell;
